import React, { useCallback, useEffect } from 'react';
import { withWrapper } from '@shared/react';
import {
    BuySellAdjustmentTableProvider,
    useClientDetailsTable,
    getTransactionsTableColumns,
    UserManagement_TABLE_INFO_URL,
} from './lib';
import { Table } from '@features/Table';
import './BuySellAdjustmentTable.scss';
import { Loader } from '@shared/UI';
import { BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis, Tooltip, Bar } from 'recharts';

interface BuySellAdjustmentTableContentProps {
    accountId: number;
    setDataGetter?: React.Dispatch<React.SetStateAction<() => any[]>>;
}

const BuySellAdjustmentTableContent: React.FC<BuySellAdjustmentTableContentProps> = ({ accountId, setDataGetter }) => {
    const { AssignPositionBody, paginationOptions, setAccountId, holdingLoading } = useClientDetailsTable();

    useEffect(() => {
        if (accountId === 0 || accountId) {
            setAccountId(accountId);
        }
    }, [accountId, setAccountId]);

    useEffect(() => {
        if (setDataGetter) {
            setDataGetter(() => () => AssignPositionBody); // Proper function return
        }
    }, [AssignPositionBody, setDataGetter]);

    const formatLargeNumber = useCallback((value: number) => {
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
        return `$${value}`;
    }, []);

    if (holdingLoading) {
        return <Loader />;
    }

    return (
        <div>
            {AssignPositionBody.length === 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '500px',
                        textAlign: 'center',
                    }}
                >
                    <img
                        src='/NoData.png'
                        alt='No data'
                        style={{
                            maxWidth: '200px',
                            opacity: 0.7,
                            marginBottom: '1rem',
                        }}
                    />
                    <span style={{ fontSize: '1rem', color: '#6b7280' }}>No data available</span>
                </div>
            )}

            {AssignPositionBody.length > 0 && (
                <div className='buy-sell-graph'>
                    <div className='losses-section'>
                        <div className='losses-title'>Securities Graph</div>
                        <div className='losses-legend'>
                            <div className='losses-legend-item'>
                                <span className='losses-legend-color losses-realized'></span>
                                <span className='losses-legend-label'>Current Value</span>
                            </div>
                            <div className='losses-legend-item'>
                                <span className='losses-legend-color losses-unrealized'></span>
                                <span className='losses-legend-label'>Rebalanced Value</span>
                            </div>
                        </div>
                    </div>
                    <div className='stock-chart'>
                        <ResponsiveContainer
                            width='100%'
                            height={150}
                        >
                            <BarChart
                                data={AssignPositionBody}
                                margin={{ top: 10, right: 10, left: -20, bottom: -10 }}
                            >
                                <CartesianGrid
                                    vertical={false}
                                    strokeDasharray='3 3'
                                    stroke='rgba(0, 0, 0, 0.1490196078)'
                                />
                                <XAxis
                                    dataKey='symbol'
                                    axisLine={false}
                                    tickLine={false}
                                    interval={0}
                                    tick={{ fontSize: 10 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={formatLargeNumber}
                                    tick={{ fontSize: 10 }}
                                />
                                <Tooltip
                                    formatter={(value: number, name: string) => {
                                        return [`$${value.toLocaleString()}`, name];
                                    }}
                                    labelFormatter={(label: string) => `${label}`}
                                    cursor={{ fill: 'transparent' }}
                                />
                                <Bar
                                    dataKey='currentValue'
                                    fill='#0066A6'
                                    name='Current Value'
                                    barSize={12}
                                    radius={[6, 6, 6, 6]}
                                />
                                <Bar
                                    dataKey='rebalancedValue'
                                    fill='#FF9E69'
                                    name='Rebalanced Value'
                                    barSize={12}
                                    radius={[6, 6, 6, 6]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {AssignPositionBody.length > 0 && (
                <div className='buy-sell-table-div'>
                    <Table
                        columns={getTransactionsTableColumns()}
                        data={AssignPositionBody}
                        tableTitle={undefined}
                        infoLink={UserManagement_TABLE_INFO_URL}
                        paginationOptions={paginationOptions}
                        filters={undefined}
                        row={undefined}
                        noScroll={true}
                    />
                </div>
            )}
        </div>
    );
};

export const BuySellAdjustmentTable = withWrapper(BuySellAdjustmentTableProvider)(BuySellAdjustmentTableContent);
